import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Shared layout for /privacy, /terms, /contact.
 * Lightweight: header bar + scrollable content + back link + minimal footer.
 * Content is passed as `children` (already translated).
 */
export default function LegalPage({ titleKey, children, testIdSuffix }) {
  const { t } = useTranslation();

  useEffect(() => {
    // Scroll to top on mount so anchored links land cleanly
    window.scrollTo(0, 0);
    document.title = `${t(titleKey)} — Powersure Worldwide`;
  }, [t, titleKey]);

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid={`legal-page-${testIdSuffix}`}>
      <header className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="legal-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Powersure Worldwide</span>
          </Link>
          <span className="text-xs text-muted-foreground hidden sm:inline">{t(titleKey)}</span>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-6 py-10 sm:py-14 max-w-3xl">
        <h1
          className="text-3xl sm:text-4xl font-light mb-8 text-foreground"
          style={{ fontFamily: 'Outfit' }}
          data-testid={`legal-title-${testIdSuffix}`}
        >
          {t(titleKey)}
        </h1>
        <div className="prose prose-sm sm:prose-base max-w-none text-foreground/90 space-y-4 leading-relaxed">
          {children}
        </div>
      </main>

      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        <div className="flex justify-center gap-4 flex-wrap">
          <Link to="/privacy" className="hover:text-foreground" data-testid="legal-footer-privacy">{t('legal.nav.privacy')}</Link>
          <span aria-hidden>·</span>
          <Link to="/terms" className="hover:text-foreground" data-testid="legal-footer-terms">{t('legal.nav.terms')}</Link>
          <span aria-hidden>·</span>
          <Link to="/contact" className="hover:text-foreground" data-testid="legal-footer-contact">{t('legal.nav.contact')}</Link>
          <span aria-hidden>·</span>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event('powersure:open-cookie-prefs'))}
            className="hover:text-foreground bg-transparent border-0 p-0 m-0 cursor-pointer"
            data-testid="legal-footer-cookie-settings"
          >
            {t('legal.nav.cookieSettings')}
          </button>
        </div>
        <p className="mt-3">&copy; {new Date().getFullYear()} Powersure Worldwide</p>
      </footer>
    </div>
  );
}
